import * as React from 'react'

import { useLocation, useNavigate } from 'react-router-dom'

import { previousHistoryEntry } from '@/lib/historyEntries'

type ReferrerState = {
    referrer?: string[]
    [key: string]: unknown
}

export function getReferrerBackTarget(state: ReferrerState | null | undefined) {
    if (!Array.isArray(state?.referrer) || state.referrer.length === 0) {
        return null
    }

    const referrer = [...state.referrer]
    const target = referrer.pop()

    if (!target) {
        return null
    }

    return {
        target,
        state: {
            ...state,
            referrer
        }
    }
}

/**
 * Where a back control goes: one step back through history when the entry
 * behind is the referrer (or there is no referrer at all), otherwise a push
 * of the referrer (deep links, reloads).
 */
export function resolveBack(
    state: ReferrerState | null | undefined,
    previous: string | undefined
):
    | { kind: 'history' }
    | { kind: 'push'; target: string; state: ReferrerState } {
    const target = getReferrerBackTarget(state)
    if (!target || previous === target.target) return { kind: 'history' }
    return { kind: 'push', target: target.target, state: target.state }
}

/**
 * The single back action for every back control on a page (header arrow,
 * ActionBar cancel, secondary "back" buttons), so they all land on the
 * same screen and history does not fill up with A, B, A.
 */
export function useReferrerBack() {
    const location = useLocation()
    const navigate = useNavigate()

    return React.useCallback(() => {
        const back = resolveBack(
            (location.state as ReferrerState | undefined) || undefined,
            previousHistoryEntry()
        )

        if (back.kind === 'push') {
            navigate(back.target, { state: back.state })
            return
        }

        navigate(-1)
    }, [location.state, navigate])
}
